import type { HeaderNavAudienceApiParam } from "@/react/components/SettingsApp/Navigation/hooks/useFetchHeaderNavItems";

import type { Params } from "../toQueryString";
import { toQueryString } from "../toQueryString";
import { absolutePath } from "./absolutePath";
import { settingsSites } from "./settingsSites";

interface SettingsFnProps {
  id?: string | number;
  options?: Record<string, unknown>;
  params?: Record<string, unknown> | object;
  uuid?: string | number;
}

interface AudienceParams {
  audience?: HeaderNavAudienceApiParam;
}

export const settings = {
  home: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("../settings/home", params),
    update: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("../settings/home", params),
  },
  landingPage: {
    welcomeBanner: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath(
          "../settings/landing_page/welcome_banner_preference",
          params,
        ),
    },
  },
  attachedFiles: {
    destroy: () => absolutePath("../settings/communities/attached_files"),
  },
  openGraphImage: {
    destroy: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`../opengraph_images/${uuid}`, params),
  },
  courses: {
    lessons: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/courses/lessons", params),
      show: ({ id, params = {} }: SettingsFnProps = {}) =>
        absolutePath(`settings/courses/lessons/${id}`, params),
    },
    quizzes: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/courses/quizzes", params),
    },
    sections: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/courses/sections", params),
      show: ({ id, params = {} }: SettingsFnProps = {}) =>
        absolutePath(`settings/courses/sections/${id}`, params),
    },
  },
  posts: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/posts", params),
  },
  events: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/events", params),
  },
  analytics: {
    analyticsIframe: ({ params = {}, options = {} }: SettingsFnProps) =>
      absolutePath("settings/analytics_iframe", params, options),
    analyticsExport: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/analytics_exports", params),
  },
  mobileApp: {
    show: () => absolutePath("settings/mobile_app"),
    update: () => absolutePath("settings/mobile_app"),
  },
  reviewSettings: {
    show: () => absolutePath("settings/review_settings"),
    update: () => absolutePath("settings/review_settings"),
  },
  mobileHomeSegments: {
    update: (id: string | number) =>
      absolutePath(`settings/mobile_home_segments/${id}`),
  },
  memberTags: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/member_tags", params),
    show: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/member_tags/${uuid}`, params),
    create: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/member_tags", params),
    update: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/member_tags/${uuid}`, params),
    destroy: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/member_tags/${uuid}`, params),
  },
  postBulkActions: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("posts/bulk_actions", params),
    show: ({ id, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`posts/bulk_actions/${id}`, params),
    create: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("posts/bulk_actions", params),
  },
  topics: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/topics", params),
    show: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/topics/${uuid}`, params),
    create: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/topics", params),
    update: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/topics/${uuid}`, params),
    destroy: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/topics/${uuid}`, params),
  },
  api: {
    index: ({ params = {} } = {}) => absolutePath("settings/api", params),
    revoke: ({ uuid, params = {} }: SettingsFnProps = {}) =>
      absolutePath(`settings/api/${uuid}/revoke`, params),
  },
  activityLogs: {
    index: ({ params = {} }: SettingsFnProps = {}) =>
      absolutePath("settings/activity_logs", params),
    exportCsv: (params: Record<string, unknown> | Params = {}) =>
      `/settings/members/activity_logs/export.csv?${toQueryString(params)}`,
    entityPath: ({ id }: SettingsFnProps = {}) =>
      `/settings/members/activity_logs/${id}/entity_path`,
    sourcePath: ({ id }: SettingsFnProps = {}) =>
      `/settings/members/activity_logs/${id}/source_path`,
  },
  adminDashboard: {
    recentlyEdited: {
      index: () => absolutePath("settings/admin_dashboard/recently_edited"),
    },
    latestFromCircle: {
      index: () => absolutePath("settings/admin_dashboard/latest_from_circle"),
    },
    discoverInterest: {
      create: () => absolutePath("settings/admin_dashboard/discover_interest"),
    },
  },
  v4_ui_preference: {
    activate: () => "/settings/v4/activate",
    joinWaitlist: () => "/settings/v4/join_waitlist",
    rollback: () => "/settings/v4/rollback",
  },
  legal: {
    show: () => absolutePath(`settings/legal`),
    update: () => absolutePath(`settings/legal`),
  },
  navigation: {
    list: ({ params = {} }: { params?: AudienceParams } = {}) =>
      absolutePath(`settings/header_menu_links`, params),
    reorder: ({ params = {} }: { params?: AudienceParams } = {}) =>
      absolutePath(`settings/header_menu_links/reorder`, params),
    create: () => absolutePath(`settings/header_menu_links`),
    edit: ({ id }: SettingsFnProps = {}) =>
      absolutePath(`settings/header_menu_links/${id}`),
    show: ({ id }: SettingsFnProps = {}) =>
      absolutePath(`settings/header_menu_links/${id}`),
    destroy: ({ id }: SettingsFnProps = {}) =>
      absolutePath(`settings/header_menu_links/${id}`),
  },
  storage: {
    metrics: {
      show: () => absolutePath("settings/storage/metrics"),
    },
    files: {
      index: ({ params = {} }: SettingsFnProps = {}) =>
        absolutePath("settings/storage/files", params),
      destroy: ({
        id,
        params = {},
      }: SettingsFnProps & { id: string | number }) =>
        absolutePath(`settings/storage/files/${id}`, params),
    },
  },
  recap: {
    get: ({ params = {} }: { params?: Params } = {}) =>
      absolutePath("communities/recap", params),
  },
  sites: settingsSites,
};
